import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { DisplayDevice } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { useSite } from '../../lib/site';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, CheckboxField, ConfirmButton, EmptyState, PageHeader, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';

const BASE = '/reception-display';
const DisplayList = z.array(DisplayDevice);
const LISTKEY = ['display-admin', 'list'] as const;
const NAMES = ['first_name', 'first_name_company', 'none'] as const;

/** "123456789" typed with or without spaces -> digits only. */
const digits = (v: string): string => v.replace(/\D/g, '').slice(0, 9);

function AddDisplayCard({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { sites, siteId } = useSite();
  const [code, setCode] = useState('');
  const [site, setSite] = useState(siteId ?? '');
  const [name, setName] = useState('');
  const chosenSite = site || sites[0]?.id || '';
  const claim = useMutation({
    mutationFn: () => api.post(`${BASE}/claim`, { body: { code, siteId: chosenSite, name: name.trim() }, schema: DisplayDevice }),
    onSuccess: async (d) => {
      await qc.invalidateQueries({ queryKey: ['display-admin'] });
      toast(t('displayUi.add.done', { name: d.name }));
      onDone();
    },
  });
  const errs = fieldErrors(claim.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    claim.mutate();
  };
  return (
    <Card title={t('displayUi.add.title')} description={t('displayUi.add.description')} className="mb-6">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-3">
        <div className="sm:col-span-3">
          <ErrorLine error={claim.error} />
        </div>
        <TextField
          label={t('displayUi.add.code')}
          hint={t('displayUi.add.codeHint')}
          inputMode="numeric"
          autoComplete="off"
          required
          value={code}
          onChange={(e) => setCode(digits(e.target.value))}
          error={errs['code']}
        />
        <SelectField label={t('displayUi.add.site')} value={chosenSite} onChange={(e) => setSite(e.target.value)} error={errs['siteId']}>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <TextField label={t('displayUi.add.name')} required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} error={errs['name']} />
        <div className="flex items-end gap-2 sm:col-span-3">
          <Button type="submit" variant="primary" disabled={claim.isPending || code.length !== 9 || name.trim() === '' || !chosenSite}>
            {t('displayUi.add.submit')}
          </Button>
          <Button onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

function SettingsCard({ display, canEdit, onClose }: { display: DisplayDevice; canEdit: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [content, setContent] = useState(display.content);
  const [message, setMessage] = useState(display.message);
  const save = useMutation({
    mutationFn: () => api.put(`${BASE}/displays/${display.deviceId}`, { body: { ...content, message }, schema: DisplayDevice }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['display-admin'] });
      toast(t('displayUi.settings.saved'));
    },
  });
  const errs = fieldErrors(save.error);
  const set = <K extends keyof typeof content>(k: K, v: (typeof content)[K]) => setContent((c) => ({ ...c, [k]: v }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Card title={t('displayUi.settings.title', { name: display.name })} description={t('displayUi.settings.siteDefaults')} className="mb-6">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <ErrorLine error={save.error} />
        </div>
        <CheckboxField label={t('displayUi.settings.welcome')} checked={content.welcome} disabled={!canEdit} onChange={(e) => set('welcome', e.target.checked)} />
        <TextField
          label={t('displayUi.settings.window')}
          type="number"
          min={0}
          max={240}
          disabled={!canEdit || !content.welcome}
          value={content.welcomeWindowMinutes}
          onChange={(e) => set('welcomeWindowMinutes', Math.max(0, Math.min(240, Math.trunc(Number(e.target.value) || 0))))}
          error={errs['welcomeWindowMinutes']}
        />
        <SelectField label={t('displayUi.settings.names')} value={content.names} disabled={!canEdit || !content.welcome} onChange={(e) => set('names', e.target.value as (typeof NAMES)[number])}>
          {NAMES.map((n) => (
            <option key={n} value={n}>
              {t(`displayUi.settings.names.${n}`)}
            </option>
          ))}
        </SelectField>
        <div className="space-y-3">
          <CheckboxField label={t('displayUi.settings.headcount')} checked={content.headcount} disabled={!canEdit} onChange={(e) => set('headcount', e.target.checked)} />
          <CheckboxField label={t('displayUi.settings.capacity')} checked={content.capacity} disabled={!canEdit || !content.headcount} onChange={(e) => set('capacity', e.target.checked)} />
          <CheckboxField label={t('displayUi.settings.notices')} checked={content.notices} disabled={!canEdit} onChange={(e) => set('notices', e.target.checked)} />
          <CheckboxField label={t('displayUi.settings.clock')} checked={content.clock} disabled={!canEdit} onChange={(e) => set('clock', e.target.checked)} />
        </div>
        <TextField
          className="sm:col-span-2"
          label={t('displayUi.settings.message')}
          hint={t('displayUi.settings.messageHint')}
          maxLength={300}
          disabled={!canEdit}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          error={errs['message']}
        />
        <div className="flex gap-2 sm:col-span-2">
          {canEdit ? (
            <Button type="submit" variant="primary" disabled={save.isPending}>
              {t('displayUi.settings.save')}
            </Button>
          ) : null}
          <Button onClick={onClose}>{t('displayUi.settings.close')}</Button>
        </div>
      </form>
    </Card>
  );
}

/** Manage -> Reception displays: add a screen by its code, choose what it shows, unpair it. */
export function ManageDisplaysPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const canManage = useCan('displays.manage');
  const { sites } = useSite();
  const [siteFilter, setSiteFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const list = useQuery({
    queryKey: [...LISTKEY, siteFilter],
    queryFn: () => api.get(`${BASE}/displays`, { query: { siteId: siteFilter || undefined }, schema: DisplayList }),
  });
  const unpair = useMutation({
    mutationFn: (d: DisplayDevice) => api.post(`${BASE}/displays/${d.deviceId}/unpair`),
    onSuccess: async (_r, d) => {
      if (editing === d.deviceId) setEditing(null);
      await qc.invalidateQueries({ queryKey: ['display-admin'] });
      toast(t('displayUi.manage.unpaired', { name: d.name }));
    },
  });
  const siteName = (id: string): string => sites.find((s) => s.id === id)?.name ?? '';
  const rows = list.data ?? [];
  const current = rows.find((d) => d.deviceId === editing && d.status === 'active');

  return (
    <>
      <PageHeader
        title={t('displayUi.manage.title')}
        description={t('displayUi.manage.description')}
        actions={
          canManage && !adding ? (
            <Button variant="primary" onClick={() => setAdding(true)}>
              {t('displayUi.manage.add')}
            </Button>
          ) : null
        }
      />
      {adding ? <AddDisplayCard onDone={() => setAdding(false)} /> : null}
      {current ? <SettingsCard key={current.deviceId} display={current} canEdit={canManage} onClose={() => setEditing(null)} /> : null}
      <div className="mb-4 max-w-xs">
        <SelectField label={t('displayUi.manage.filterSite')} value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}>
          <option value="">{t('displayUi.manage.allSites')}</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
      </div>
      <ErrorLine error={unpair.error} />
      {list.isError ? (
        <ErrorPanel error={list.error} onRetry={() => void list.refetch()} />
      ) : !list.data ? (
        <Spinner />
      ) : (
        <TableWrap caption={t('displayUi.manage.table')}>
          <thead>
            <tr>
              <Th>{t('displayUi.manage.col.name')}</Th>
              <Th>{t('displayUi.manage.col.site')}</Th>
              <Th>{t('displayUi.manage.col.lastSeen')}</Th>
              <Th>{t('displayUi.manage.col.status')}</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5}><EmptyState message={t('displayUi.manage.empty')} /></td></tr>
            ) : (
              rows.map((d) => (
                <tr key={d.deviceId} className="border-t border-slate-200">
                  <td className="px-4 py-3 font-medium">{d.name}</td>
                  <td className="px-4 py-3">{siteName(d.siteId)}</td>
                  <td className="px-4 py-3">{d.lastSeenAt ? formatDateTime(d.lastSeenAt) : t('displayUi.manage.neverSeen')}</td>
                  <td className="px-4 py-3">
                    <Badge tone={d.status === 'active' ? 'good' : 'neutral'}>{t(`displayUi.manage.status.${d.status}`)}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    {d.status === 'active' ? (
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button aria-label={t('displayUi.manage.edit', { name: d.name })} onClick={() => setEditing(d.deviceId)}>
                          {t('common.edit')}
                        </Button>
                        {canManage ? (
                          <ConfirmButton
                            label={t('displayUi.manage.unpair', { name: d.name })}
                            pending={unpair.isPending}
                            onConfirm={() => unpair.mutate(d)}
                          />
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </TableWrap>
      )}
    </>
  );
}
